import React, { useEffect, useState, useMemo } from 'react';
import { db, collection, onSnapshot, query, where, handleFirestoreError, OperationType, limit, updateDoc, doc, Timestamp, addDoc, deleteDoc } from '../firebase';
import { Task, UserProfile, Report, Client, Branch, ReportSparePart, ReportStatus, ReportType, JobCardRequest, JobCard, SiteSurveyItem, TechnicalReportItem } from '../types';
import { useAuth } from '../App';
import { 
  Zap, 
  ClipboardList, 
  Search, 
  Filter, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  ChevronRight,
  User as UserIcon,
  MessageSquare,
  FileText,
  Plus,
  Trash2,
  Save,
  Send,
  Wrench,
  Calendar,
  X,
  Loader2,
  Check,
  Edit3,
  Eye,
  Download,
  ChevronDown,
  Layers,
  Settings,
  Activity,
  Building2,
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link,
  Image as ImageIcon,
  Table as TableIcon,
  Code,
  Maximize2,
  HelpCircle,
  Sparkles,
  Printer,
  ArrowRight,
  ExternalLink,
  FileSpreadsheet,
  CheckSquare,
  Share2,
  FileCheck,
  SendHorizontal
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { toast } from 'sonner';
import { ServiceScheduleViewerModal } from '../components/ServiceScheduleViewerModal';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Default empty arrays for system data
const DEFAULT_SITE_SURVEYS: SiteSurveyItem[] = [];
const DEFAULT_TECHNICAL_REPORTS: TechnicalReportItem[] = [];

const TechnicalDeptPage: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const { profile, isAdmin, isManager, isTechnician } = useAuth();

  // Navigation module tabs
  const [activeTab, setActiveTab] = useState<'tasks' | 'technicians' | 'site_survey' | 'site_survey_report' | 'technical_report' | 'jobcards' | 'reports'>('site_survey');
  const [siteSurveySubTab, setSiteSurveySubTab] = useState<'active' | 'approved'>('active');
  const [technicalReportSubTab, setTechnicalReportSubTab] = useState<'active' | 'approved'>('active');

  // Firestore & local state for site surveys and technical reports
  const [siteSurveys, setSiteSurveys] = useState<SiteSurveyItem[]>(DEFAULT_SITE_SURVEYS);
  const [siteSurveyReports, setSiteSurveyReports] = useState<SiteSurveyItem[]>([]);
  const [technicalReports, setTechnicalReports] = useState<TechnicalReportItem[]>(DEFAULT_TECHNICAL_REPORTS);
  const [isScheduleViewerOpen, setIsScheduleViewerOpen] = useState(false);

  // Active Dropdown state (row action dropdowns)
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);

  // Modal States
  const [isCreateSurveyModalOpen, setIsCreateSurveyModalOpen] = useState(false);
  const [createSurveyFormData, setCreateSurveyFormData] = useState({
    clientRequest: '',
    siteName: '',
    technicianName: profile?.displayName || 'MICHAEL CHUWA',
    typeOfWork: 'Inspection & Repair',
    machineType: 'AIR CONDITIONERS',
    quantity: '1',
    userDepartment: 'Branch Operations',
    checkingResults: '',
    remedialRecommendation: ''
  });

  const [viewingSurvey, setViewingSurvey] = useState<SiteSurveyItem | null>(null);
  const [editingSurvey, setEditingSurvey] = useState<SiteSurveyItem | null>(null);
  const [viewingTechReport, setViewingTechReport] = useState<TechnicalReportItem | null>(null);
  const [editingTechReport, setEditingTechReport] = useState<TechnicalReportItem | null>(null);
  const [viewingProforma, setViewingProforma] = useState<TechnicalReportItem | null>(null);
  const [viewingTechnicianResponse, setViewingTechnicianResponse] = useState<Task | null>(null);

  // Forward Report Modal States
  const [forwardingItem, setForwardingItem] = useState<SiteSurveyItem | TechnicalReportItem | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('Billing & Commercial Department');
  const [forwardNotes, setForwardNotes] = useState<string>('');

  // Job Card & Report Review States
  const [selectedJobCard, setSelectedJobCard] = useState<any | null>(null);
  const [isNewJobCardModalOpen, setIsNewJobCardModalOpen] = useState<boolean>(false);

  // Request Job Card modal states
  const [isRequestJobCardModalOpen, setIsRequestJobCardModalOpen] = useState(false);
  const [reqJcClientId, setReqJcClientId] = useState('');
  const [reqJcBranchName, setReqJcBranchName] = useState('');
  const [reqJcFindings, setReqJcFindings] = useState('');
  const [reqJcSubmitting, setReqJcSubmitting] = useState(false);

  const handleRequestJobCardSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reqJcClientId) {
      toast.error('Please select a client');
      return;
    }
    if (!reqJcFindings.trim()) {
      toast.error('Please enter findings / scope description');
      return;
    }

    const selectedClient = clients.find(c => c.id === reqJcClientId);
    setReqJcSubmitting(true);
    try {
      await addDoc(collection(db, 'job_card_requests'), {
        clientId: reqJcClientId,
        clientName: selectedClient ? selectedClient.name : 'Client',
        branchName: reqJcBranchName || 'Main Branch',
        findings: reqJcFindings,
        requestedBy: profile?.displayName || profile?.email || 'Technical Staff',
        requestedById: profile?.uid || 'unknown',
        status: 'pending',
        createdAt: Timestamp.now()
      });

      toast.success('Job Card requested successfully! Request sent to Billing.');
      setIsRequestJobCardModalOpen(false);
      setReqJcClientId('');
      setReqJcBranchName('');
      setReqJcFindings('');
    } catch (error) {
      console.error('Error requesting job card:', error);
      toast.error('Failed to request Job Card');
    } finally {
      setReqJcSubmitting(false);
    }
  };

  const [jobCardRequests, setJobCardRequests] = useState<JobCardRequest[]>([]);

  // Manage Job Card Request Table States & Logic
  const [jobCardSearchTerm, setJobCardSearchTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<'sn' | 'siteName' | 'clientRequest' | 'jobCardNo' | 'status'>('sn');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (column: 'sn' | 'siteName' | 'clientRequest' | 'jobCardNo' | 'status') => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const combinedJobCardList = useMemo(() => {
    const realList = jobCardRequests.map((req, index) => ({
      id: req.id,
      sn: index + 1,
      siteName: req.branchName ? `${req.clientName} (${req.branchName})` : req.clientName || 'Site Name',
      clientRequest: req.findings || 'Site Maintenance Request',
      jobCardNo: req.jobCardNo ? String(req.jobCardNo).padStart(8, '0') : `0000${4680 - index}`,
      status: req.status === 'completed' ? 'Confirmed' : 'Confirmed'
    }));

    const fullList = realList;

    // Search filter
    const filtered = fullList.filter(item => {
      const term = jobCardSearchTerm.toLowerCase().trim();
      if (!term) return true;
      return (
        item.siteName.toLowerCase().includes(term) ||
        item.clientRequest.toLowerCase().includes(term) ||
        item.jobCardNo.toLowerCase().includes(term) ||
        item.status.toLowerCase().includes(term)
      );
    });

    // Reassign S/N sequentially
    const withSN = filtered.map((item, idx) => ({ ...item, sn: idx + 1 }));

    // Sort
    withSN.sort((a, b) => {
      let aVal: any = a[sortColumn];
      let bVal: any = b[sortColumn];

      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return withSN;
  }, [jobCardRequests, jobCardSearchTerm, sortColumn, sortDirection]);

  const totalEntries = combinedJobCardList.length;
  const paginatedJobCards = combinedJobCardList.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );
  const [surveyReports, setSurveyReports] = useState<Report[]>([]);
  const [selectedSurveyReportId, setSelectedSurveyReportId] = useState('');

  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [selectedTaskForReport, setSelectedTaskForReport] = useState<Task | null>(null);
  const [isPartSelectorOpen, setIsPartSelectorOpen] = useState(false);
  const [partSearchTerm, setPartSearchTerm] = useState('');
  const [clientSpareParts, setClientSpareParts] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [reportFormData, setReportFormData] = useState({
    equipment: [] as { name: string, model: string, serialNumber: string, status: string }[],
    findings: '',
    recommendations: '',
    comments: '',
    requiredSpareParts: [] as ReportSparePart[],
    type: 'general' as ReportType,
    status: 'submitted_to_technical' as ReportStatus,
  });

  // Load Firestore data and listen for changes
  useEffect(() => {
    const q = query(collection(db, 'tasks'), where('type', '==', 'technical'));
    const unsubscribeTasks = onSnapshot(q, (snapshot) => {
      setTasks(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Task)));
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'tasks');
      setLoading(false);
    });

    const reportsQuery = query(
      collection(db, 'reports'), 
      where('status', 'in', ['submitted_to_technical', 'arranged_by_technical', 'technical_review', 'manager_review', 'technical_survey_report'])
    );
    const unsubscribeReports = onSnapshot(reportsQuery, (snapshot) => {
      setReports(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Report)));
    });

    const unsubscribeClients = onSnapshot(collection(db, 'clients'), (snapshot) => {
      setClients(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Client)));
    });

    const unsubscribeJobCardRequests = onSnapshot(collection(db, 'job_card_requests'), (snapshot) => {
      setJobCardRequests(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as JobCardRequest)));
    });

    const surveyReportsQuery = query(
      collection(db, 'reports'),
      where('status', '==', 'technical_survey_report')
    );
    const unsubscribeSurveyReports = onSnapshot(surveyReportsQuery, (snapshot) => {
      setSurveyReports(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Report)));
    });

    const unsubscribeSettings = onSnapshot(query(collection(db, 'settings'), limit(1)), (snapshot) => {
      if (!snapshot.empty) {
        setLogoUrl(snapshot.docs[0].data().logoUrl || null);
      }
    });

    // Subscribe to site_surveys collection in Firestore
    const unsubscribeSiteSurveys = onSnapshot(collection(db, 'site_surveys'), (snapshot) => {
      const firestoreSurveys = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SiteSurveyItem));
      setSiteSurveys(firestoreSurveys);
    });

    // Subscribe to technical_reports collection in Firestore
    const unsubscribeTechReports = onSnapshot(collection(db, 'technical_reports'), (snapshot) => {
      const firestoreTech = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as TechnicalReportItem));
      setTechnicalReports(firestoreTech);
    });

    return () => {
      unsubscribeTasks();
      unsubscribeReports();
      unsubscribeClients();
      unsubscribeJobCardRequests();
      unsubscribeSurveyReports();
      unsubscribeSettings();
      unsubscribeSiteSurveys();
      unsubscribeTechReports();
    };
  }, []);

  useEffect(() => {
    const clientId = selectedReport?.clientId || selectedTaskForReport?.clientId;
    if (clientId) {
      const reportsQuery = query(collection(db, 'reports'), where('clientId', '==', clientId));
      const sparePartsRef = collection(db, 'clients', clientId, 'spareParts');
      
      const unsubscribeParts = onSnapshot(reportsQuery, (snapshot) => {
        const reportParts: any[] = [];
        snapshot.docs.forEach(doc => {
          const report = doc.data() as Report;
          if (report.requiredSpareParts) {
            report.requiredSpareParts.forEach(p => {
              if (!reportParts.find(existing => existing.name === p.name)) {
                reportParts.push({ ...p, id: `report-${p.name}` });
              }
            });
          }
        });
        
        const unsubscribeDedicated = onSnapshot(sparePartsRef, (spareSnapshot) => {
          const dedicatedParts = spareSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));
          const allParts = [...reportParts];
          dedicatedParts.forEach(p => {
            if (!allParts.find(existing => (existing as any).name === p.name)) {
              allParts.push(p);
            }
          });
          setClientSpareParts(allParts);
        });

        return () => unsubscribeDedicated();
      });

      return () => unsubscribeParts();
    }
  }, [selectedReport, selectedTaskForReport]);

  // Handle Create Survey Form Submit
  const handleCreateSurveySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createSurveyFormData.clientRequest) {
      toast.error('Please enter or select a Client Request');
      return;
    }

    const regNo = Math.floor(2500 + Math.random() * 200);
    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const formattedDateTime = today.toISOString().replace('T', ' ').substring(0, 19);

    const fullCheckingResult = `REG NO # ${regNo} DATED ${formattedDate}.\n\n${createSurveyFormData.siteName || createSurveyFormData.clientRequest.substring(0, 30)}:\n\n${createSurveyFormData.checkingResults || 'INSPECTION COMPLETED AS REQUESTED.'}`;

    const newSurvey: Omit<SiteSurveyItem, 'id'> = {
      clientRequest: createSurveyFormData.clientRequest,
      siteName: createSurveyFormData.siteName || createSurveyFormData.clientRequest.split(' ')[0] + ' BRANCH',
      machineType: createSurveyFormData.machineType || 'AIR CONDITIONERS.',
      technicianName: createSurveyFormData.technicianName || 'MICHAEL CHUWA.',
      typeOfWork: createSurveyFormData.typeOfWork,
      quantity: createSurveyFormData.quantity,
      userDepartment: createSurveyFormData.userDepartment,
      checkingResult: fullCheckingResult,
      remedialRecommendation: createSurveyFormData.remedialRecommendation || 'Recommended appropriate equipment upgrade.',
      status: 'On Progress',
      createdAt: formattedDateTime,
      requestedBy: profile?.displayName || 'Emmanuel Lupatu',
      isApproved: false
    };

    try {
      const docRef = await addDoc(collection(db, 'site_surveys'), newSurvey);
      setSiteSurveys(prev => [{ id: docRef.id, ...newSurvey }, ...prev]);
      toast.success('Site survey created successfully!');
      setIsCreateSurveyModalOpen(false);
      setCreateSurveyFormData({
        clientRequest: '',
        siteName: '',
        technicianName: profile?.displayName || 'MICHAEL CHUWA',
        typeOfWork: 'Inspection & Repair',
        machineType: 'AIR CONDITIONERS',
        quantity: '1',
        userDepartment: 'Branch Operations',
        checkingResults: '',
        remedialRecommendation: ''
      });
    } catch (err) {
      console.error(err);
      // Fallback local update
      const localId = `survey-${Date.now()}`;
      setSiteSurveys(prev => [{ id: localId, ...newSurvey }, ...prev]);
      toast.success('Site survey added locally!');
      setIsCreateSurveyModalOpen(false);
    }
  };

  // Approve Survey Handler
  const handleApproveSurvey = async (survey: SiteSurveyItem) => {
    const todayStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' + new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    try {
      await updateDoc(doc(db, 'site_surveys', survey.id), {
        status: 'Approved',
        isApproved: true,
        approvedAt: todayStr,
        approvedBy: profile?.displayName || 'HOD Technical'
      });
      toast.success('Survey report marked as Approved');
    } catch (e) {
      setSiteSurveys(prev => prev.map(s => s.id === survey.id ? { ...s, status: 'Approved', isApproved: true, approvedAt: todayStr } : s));
      toast.success('Survey report approved');
    }
    setActiveDropdownId(null);
  };

  // Forward Survey / Technical Report Handler - Opens Department Selection Modal
  const handleForwardSurveyReport = (survey: SiteSurveyItem | TechnicalReportItem) => {
    setForwardingItem(survey);
    setSelectedDepartment('Billing & Commercial Department');
    setForwardNotes('');
    setActiveDropdownId(null);
  };

  const handleConfirmForward = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forwardingItem) return;

    const todayStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' + new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

    try {
      const isTechReport = 'proformaNo' in forwardingItem;
      const collectionName = isTechReport ? 'technical_reports' : 'site_surveys';

      await updateDoc(doc(db, collectionName, forwardingItem.id), {
        status: 'Forwarded',
        forwardedToDepartment: selectedDepartment,
        forwardedAt: todayStr,
        forwardedBy: profile?.displayName || 'HOD Technical',
        forwardNotes: forwardNotes
      });
    } catch (err) {
      setSiteSurveys(prev => prev.map(s => s.id === forwardingItem.id ? { ...s, status: 'Forwarded', forwardedToDepartment: selectedDepartment } as any : s));
      setTechnicalReports(prev => prev.map(r => r.id === forwardingItem.id ? { ...r, status: 'Forwarded', forwardedToDepartment: selectedDepartment } as any : r));
    }

    toast.success(`Survey report for "${forwardingItem.siteName}" successfully forwarded to ${selectedDepartment}!`);
    setForwardingItem(null);
    setForwardNotes('');
    setActiveDropdownId(null);
  };

  // Approve Technical Report Handler
  const handleApproveTechnicalReport = async (report: TechnicalReportItem) => {
    const todayStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' + new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    try {
      await updateDoc(doc(db, 'technical_reports', report.id), {
        status: 'approved',
        approvedAt: todayStr,
        approvedBy: profile?.displayName || 'HOD Technical'
      });
      toast.success('Technical report approved successfully');
    } catch (e) {
      setTechnicalReports(prev => prev.map(r => r.id === report.id ? { ...r, status: 'approved', approvedAt: todayStr } : r));
      toast.success('Technical report approved');
    }
    setActiveDropdownId(null);
  };

  // Update Technical Report Handler
  const handleSaveEditTechReport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTechReport) return;
    try {
      await updateDoc(doc(db, 'technical_reports', editingTechReport.id), { ...editingTechReport });
      toast.success('Technical report updated');
    } catch (e) {
      setTechnicalReports(prev => prev.map(r => r.id === editingTechReport.id ? editingTechReport : r));
      toast.success('Technical report updated');
    }
    setEditingTechReport(null);
  };

  // Update Site Survey Handler
  const handleSaveEditSurvey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSurvey) return;
    try {
      await updateDoc(doc(db, 'site_surveys', editingSurvey.id), { ...editingSurvey });
      toast.success('Site survey updated');
    } catch (e) {
      setSiteSurveys(prev => prev.map(s => s.id === editingSurvey.id ? editingSurvey : s));
      toast.success('Site survey updated');
    }
    setEditingSurvey(null);
  };

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    try {
      await updateDoc(doc(db, 'tasks', taskId), {
        status: newStatus,
        updatedAt: Timestamp.now()
      });
      toast.success(`Task status updated to ${newStatus}`);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `tasks/${taskId}`);
      toast.error('Failed to update task status');
    }
  };

  const handleReportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTaskForReport) return;
    setIsSubmitting(true);

    const status = reportFormData.type === 'survey' ? 'technical_survey_report' : 'submitted_to_technical';

    try {
      await addDoc(collection(db, 'reports'), {
        taskId: selectedTaskForReport.id,
        clientId: selectedTaskForReport.clientId,
        clientName: selectedTaskForReport.clientName,
        branchId: selectedTaskForReport.branchId || null,
        branchName: selectedTaskForReport.branchName || null,
        technicianId: profile?.uid || '',
        technicianName: profile?.displayName || 'Technician',
        type: reportFormData.type,
        status: status,
        findings: reportFormData.findings,
        recommendations: reportFormData.recommendations,
        comments: reportFormData.comments,
        equipment: reportFormData.equipment,
        requiredSpareParts: reportFormData.requiredSpareParts,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      await updateDoc(doc(db, 'tasks', selectedTaskForReport.id), {
        status: 'pending review',
        updatedAt: Timestamp.now()
      });

      toast.success('Report submitted successfully!');
      setIsReportModalOpen(false);
      setSelectedTaskForReport(null);
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'reports');
      toast.error('Failed to submit report.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addSparePart = (partName: string, unit?: string) => {
    if (isReportModalOpen) {
      const newParts = [...(reportFormData.requiredSpareParts || []), { name: partName, quantity: 1, unit }];
      setReportFormData({ ...reportFormData, requiredSpareParts: newParts });
    } else if (selectedReport) {
      const newParts = [...(selectedReport.requiredSpareParts || []), { name: partName, quantity: 1, unit }];
      setSelectedReport({ ...selectedReport, requiredSpareParts: newParts });
    }
  };

  const removeSparePart = (index: number) => {
    if (isReportModalOpen) {
      const newParts = reportFormData.requiredSpareParts?.filter((_, i) => i !== index);
      setReportFormData({ ...reportFormData, requiredSpareParts: newParts });
    } else if (selectedReport) {
      const newParts = selectedReport.requiredSpareParts?.filter((_, i) => i !== index);
      setSelectedReport({ ...selectedReport, requiredSpareParts: newParts });
    }
  };

  const updateSparePartQty = (index: number, qty: number) => {
    if (isReportModalOpen && reportFormData.requiredSpareParts) {
      const newParts = [...reportFormData.requiredSpareParts];
      newParts[index].quantity = qty;
      setReportFormData({ ...reportFormData, requiredSpareParts: newParts });
    } else if (selectedReport && selectedReport.requiredSpareParts) {
      const newParts = [...selectedReport.requiredSpareParts];
      newParts[index].quantity = qty;
      setSelectedReport({ ...selectedReport, requiredSpareParts: newParts });
    }
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = 
      task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-500 font-bold">Loading technical dashboard...</div>;

  return (
    <div className="space-y-6">
      {/* Top Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-3xl border border-slate-200/80 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-gradient-to-tr from-purple-700 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/20 overflow-hidden text-white font-bold">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="w-full h-full object-contain p-1" referrerPolicy="no-referrer" />
            ) : (
              <Wrench className="w-7 h-7 text-white" />
            )}
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">Technical Dashboard</h1>
            <p className="text-slate-500 font-medium text-xs tracking-wide">Manage site surveys, technical reports, task responses & job cards</p>
          </div>
        </div>

        {/* Primary Module Navigation Bar */}
        <div className="flex flex-wrap items-center bg-slate-100/80 p-1.5 rounded-2xl border border-slate-200 gap-1">
          <button
            onClick={() => setActiveTab('site_survey')}
            className={cn(
              "px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer",
              activeTab === 'site_survey' ? "bg-white text-purple-900 shadow-sm font-black" : "text-slate-600 hover:text-slate-900"
            )}
          >
            <Building2 className="w-4 h-4" />
            Site Survey
          </button>

          <button
            onClick={() => setActiveTab('site_survey_report')}
            className={cn(
              "px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer",
              activeTab === 'site_survey_report' ? "bg-white text-purple-900 shadow-sm font-black" : "text-slate-600 hover:text-slate-900"
            )}
          >
            <FileSpreadsheet className="w-4 h-4" />
            Site Survey Report
          </button>

          <button
            onClick={() => setActiveTab('technical_report')}
            className={cn(
              "px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer",
              activeTab === 'technical_report' ? "bg-white text-purple-900 shadow-sm font-black" : "text-slate-600 hover:text-slate-900"
            )}
          >
            <FileText className="w-4 h-4" />
            Technical Report
          </button>

          <button
            onClick={() => setActiveTab('technicians')}
            className={cn(
              "px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer",
              activeTab === 'technicians' ? "bg-white text-purple-900 shadow-sm font-black" : "text-slate-600 hover:text-slate-900"
            )}
          >
            <UserIcon className="w-4 h-4" />
            Technicians
          </button>

          <button
            onClick={() => setActiveTab('tasks')}
            className={cn(
              "px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer",
              activeTab === 'tasks' ? "bg-white text-purple-900 shadow-sm font-black" : "text-slate-600 hover:text-slate-900"
            )}
          >
            <ClipboardList className="w-4 h-4" />
            Tasks
          </button>

          <button
            onClick={() => setActiveTab('jobcards')}
            className={cn(
              "px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer",
              activeTab === 'jobcards' ? "bg-white text-purple-900 shadow-sm font-black" : "text-slate-600 hover:text-slate-900"
            )}
          >
            <Activity className="w-4 h-4" />
            Job Cards
          </button>

          <button
            onClick={() => setActiveTab('reports')}
            className={cn(
              "px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer",
              activeTab === 'reports' ? "bg-white text-purple-900 shadow-sm font-black" : "text-slate-600 hover:text-slate-900"
            )}
          >
            <Layers className="w-4 h-4" />
            Report Review
            {reports.length > 0 && (
              <span className="bg-orange-500 text-white text-[10px] px-1.5 py-0.2 rounded-full font-bold">
                {reports.length}
              </span>
            )}
          </button>

          <button
            onClick={() => setIsScheduleViewerOpen(true)}
            className="px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer bg-indigo-900 text-white hover:bg-indigo-800 shadow-sm ml-auto"
          >
            <Calendar className="w-4 h-4 text-indigo-300" />
            <span>View Service Schedule</span>
          </button>
        </div>
      </div>

      {/* ==================== MODULE 1: SITE SURVEY MODULE ==================== */}
      {activeTab === 'site_survey' && (
        <div className="bg-slate-50/50 p-6 rounded-3xl border border-slate-200 shadow-xs space-y-6">
          {/* Module Sub-Header & Action Controls */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSiteSurveySubTab('active')}
                className={cn(
                  "px-5 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer",
                  siteSurveySubTab === 'active'
                    ? "bg-white text-purple-900 border border-slate-200 shadow-sm"
                    : "text-slate-500 hover:text-slate-800"
                )}
              >
                Active Survey
              </button>
              <button
                onClick={() => setSiteSurveySubTab('approved')}
                className={cn(
                  "px-5 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer",
                  siteSurveySubTab === 'approved'
                    ? "bg-white text-purple-900 border border-slate-200 shadow-sm"
                    : "text-slate-500 hover:text-slate-800"
                )}
              >
                Approved
              </button>
            </div>

            {siteSurveySubTab === 'active' && (
              <button
                onClick={() => setIsCreateSurveyModalOpen(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold text-sm px-4 py-2.5 rounded-xl flex items-center gap-2 shadow-md shadow-purple-600/20 transition-all cursor-pointer self-start md:self-auto"
              >
                <Plus className="w-4 h-4" />
                Add New
              </button>
            )}
          </div>

          {/* ACTIVE SITE SURVEYS TAB */}
          {siteSurveySubTab === 'active' && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex flex-col md:flex-row justify-between md:items-center gap-4 bg-slate-50/30">
                <h2 className="text-xl font-bold text-slate-800 tracking-tight">Active Site Surveys</h2>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                    <span>Show</span>
                    <select className="border border-slate-200 bg-white rounded-lg px-2 py-1 focus:outline-none">
                      <option>10</option>
                      <option>25</option>
                      <option>50</option>
                    </select>
                    <span>entries</span>
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      className="border border-slate-200 bg-white rounded-lg pl-3 pr-8 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-purple-600"
                    />
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-100/90 text-slate-700 font-bold border-b border-slate-200">
                      <th className="py-3 px-3 w-10 text-center">S/N <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[220px]">Client Request <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[140px]">Site Name <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[130px]">Machine Type <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[130px]">Technician Name <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[280px]">Checking Result <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-3 min-w-[100px]">Status <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[130px]">Created At <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[120px]">Requested By <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[100px] text-center">Actions <span className="text-slate-400 font-normal">↓↑</span></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                    {siteSurveys
                      .filter(s => !s.isApproved && s.status !== 'Approved')
                      .filter(s => 
                        s.siteName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        s.clientRequest.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        s.technicianName.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map((survey, index) => (
                        <tr key={`survey-req-${survey.id}-${index}`} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-4 px-3 text-center font-bold text-slate-500">{index + 1}</td>
                          <td className="py-4 px-4 text-slate-800 font-semibold leading-relaxed uppercase text-[11px] whitespace-pre-wrap">
                            {survey.clientRequest}
                          </td>
                          <td className="py-4 px-4 font-bold text-slate-900 uppercase text-[11px]">{survey.siteName}</td>
                          <td className="py-4 px-4 font-bold text-slate-800 uppercase text-[11px]">{survey.machineType}</td>
                          <td className="py-4 px-4 font-bold text-slate-800 uppercase text-[11px]">{survey.technicianName}</td>
                          <td className="py-4 px-4 leading-relaxed text-[11px]">
                            {survey.checkingResult.split('\n').map((line, lIdx) => {
                              if (line.startsWith('REG NO #')) {
                                return <div key={lIdx} className="font-extrabold text-blue-800 mb-1">{line}</div>;
                              }
                              return <div key={lIdx} className="text-slate-700">{line}</div>;
                            })}
                          </td>
                          <td className="py-4 px-3">
                            <span className="font-extrabold text-slate-900 text-[11px]">
                              {survey.status}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-slate-600 whitespace-nowrap text-[11px] font-mono">{survey.createdAt}</td>
                          <td className="py-4 px-4 text-slate-800 font-semibold text-[11px]">{survey.requestedBy}</td>
                          <td className="py-4 px-4 text-center relative">
                            <button
                              onClick={() => setActiveDropdownId(activeDropdownId === survey.id ? null : survey.id)}
                              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold text-xs px-3 py-1.5 rounded-lg flex items-center justify-center gap-1 shadow-sm mx-auto cursor-pointer transition-all"
                            >
                              Action <ChevronDown className="w-3 h-3" />
                            </button>

                            {/* Dropdown Menu */}
                            {activeDropdownId === survey.id && (
                              <div className="absolute right-4 top-12 z-50 w-44 bg-white rounded-xl shadow-xl border border-slate-200 py-1 text-left text-xs font-medium">
                                <button
                                  onClick={() => { setViewingSurvey(survey); setActiveDropdownId(null); }}
                                  className="w-full px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-slate-700"
                                >
                                  <Eye className="w-3.5 h-3.5 text-blue-600" /> View Details
                                </button>
                                <button
                                  onClick={() => { setEditingSurvey(survey); setActiveDropdownId(null); }}
                                  className="w-full px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-slate-700"
                                >
                                  <Edit3 className="w-3.5 h-3.5 text-orange-600" /> Edit Survey
                                </button>
                                <button
                                  onClick={() => handleApproveSurvey(survey)}
                                  className="w-full px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-emerald-700 font-bold"
                                >
                                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Approve Survey
                                </button>
                                <button
                                  onClick={() => handleForwardSurveyReport(survey)}
                                  className="w-full px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-purple-700 font-bold border-t border-slate-100"
                                >
                                  <SendHorizontal className="w-3.5 h-3.5 text-purple-600" /> Forward Report
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    {siteSurveys.filter(s => !s.isApproved && s.status !== 'Approved').length === 0 && (
                      <tr>
                        <td colSpan={10} className="py-8 text-center text-slate-400 font-bold">
                          No active site surveys found. Click "+ Add New" to create one.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="p-4 border-t border-slate-100 flex flex-col md:flex-row justify-between items-center text-xs text-slate-500 gap-2">
                <div>Showing 1 to {siteSurveys.filter(s => !s.isApproved).length} of {siteSurveys.filter(s => !s.isApproved).length} entries</div>
                <div className="flex gap-1">
                  <button className="px-3 py-1 border border-slate-200 rounded-lg hover:bg-slate-50 font-medium">Previous</button>
                  <button className="px-3 py-1 bg-purple-600 text-white rounded-lg font-medium">Next</button>
                </div>
              </div>
            </div>
          )}

          {/* APPROVED SITE SURVEYS TAB (Matching Image 3) */}
          {siteSurveySubTab === 'approved' && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex flex-col md:flex-row justify-between md:items-center gap-4 bg-slate-50/30">
                <h2 className="text-xl font-bold text-slate-800 tracking-tight">Approved Site Surveys</h2>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                    <span>Show</span>
                    <select className="border border-slate-200 bg-white rounded-lg px-2 py-1 focus:outline-none">
                      <option>10</option>
                      <option>25</option>
                    </select>
                    <span>entries</span>
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      className="border border-slate-200 bg-white rounded-lg pl-3 pr-8 py-1.5 text-xs focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-100/90 text-slate-700 font-bold border-b border-slate-200">
                      <th className="py-3 px-3 w-10 text-center">S/N <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[220px]">Client Request <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[140px]">Site Name <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[130px]">Machine Type <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[130px]">Technician Name <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[280px]">Checking Result <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[100px] text-center">Actions <span className="text-slate-400 font-normal">↓↑</span></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                    {siteSurveys
                      .filter(s => s.isApproved || s.status === 'Approved')
                      .filter(s => 
                        s.siteName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        s.clientRequest.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map((survey, index) => (
                        <tr key={`survey-rep-${survey.id}-${index}`} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-4 px-3 text-center font-bold text-slate-500">{index + 1}</td>
                          <td className="py-4 px-4 text-slate-800 font-semibold leading-relaxed uppercase text-[11px] whitespace-pre-wrap">{survey.clientRequest}</td>
                          <td className="py-4 px-4 font-bold text-slate-900 uppercase text-[11px]">{survey.siteName}</td>
                          <td className="py-4 px-4 font-bold text-slate-800 uppercase text-[11px]">{survey.machineType}</td>
                          <td className="py-4 px-4 font-bold text-slate-800 uppercase text-[11px]">{survey.technicianName}</td>
                          <td className="py-4 px-4 leading-relaxed text-[11px]">
                            {survey.checkingResult.split('\n').map((line, lIdx) => (
                              <div key={lIdx} className={line.startsWith('REG NO #') ? 'font-extrabold text-blue-800 mb-1' : 'text-slate-700'}>
                                {line}
                              </div>
                            ))}
                          </td>
                          <td className="py-4 px-4 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <button
                                onClick={() => setViewingSurvey(survey)}
                                className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold px-2.5 py-1 rounded-md shadow-xs transition-all cursor-pointer"
                              >
                                Tech
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    {siteSurveys.filter(s => s.isApproved || s.status === 'Approved').length === 0 && (
                      <tr>
                        <td colSpan={7} className="py-12 text-center text-slate-400 font-medium">
                          No data available in table
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="p-4 border-t border-slate-100 flex flex-col md:flex-row justify-between items-center text-xs text-slate-500 gap-2">
                <div>Showing 0 to {siteSurveys.filter(s => s.isApproved).length} of {siteSurveys.filter(s => s.isApproved).length} entries</div>
                <div className="flex gap-1">
                  <button className="px-3 py-1 border border-slate-200 rounded-lg hover:bg-slate-50 font-medium">Previous</button>
                  <button className="px-3 py-1 bg-purple-600 text-white rounded-lg font-medium">Next</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==================== MODULE 2: SITE SURVEY REPORT MODULE ==================== */}
      {activeTab === 'site_survey_report' && (
        <div className="bg-slate-50/50 p-6 rounded-3xl border border-slate-200 shadow-xs space-y-6">
          <div className="flex justify-between items-center border-b border-slate-200 pb-4">
            <div>
              <h2 className="text-xl font-black text-slate-900 tracking-tight">Site Survey Reports</h2>
              <p className="text-xs text-slate-500">Reports waiting to be forwarded to other departments for further processing</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/30">
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                <span>Show</span>
                <select className="border border-slate-200 bg-white rounded-lg px-2 py-1">
                  <option>10</option>
                  <option>25</option>
                </select>
                <span>entries</span>
              </div>
              <input
                type="text"
                placeholder="Search survey reports..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="border border-slate-200 bg-white rounded-lg pl-3 pr-4 py-1.5 text-xs focus:outline-none"
              />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-100/90 text-slate-700 font-bold border-b border-slate-200">
                    <th className="py-3 px-3 w-10 text-center">S/N</th>
                    <th className="py-3 px-4 min-w-[140px]">Site Name</th>
                    <th className="py-3 px-4 min-w-[220px]">Client Request</th>
                    <th className="py-3 px-3 min-w-[100px]">Proforma No</th>
                    <th className="py-3 px-3 min-w-[120px]">Machine Type</th>
                    <th className="py-3 px-4 min-w-[120px]">Technician Name</th>
                    <th className="py-3 px-4 min-w-[280px]">Checking Result</th>
                    <th className="py-3 px-4 min-w-[120px]">Created At</th>
                    <th className="py-3 px-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                  {technicalReports
                    .filter(r => r.status === 'active' || r.status === 'pending')
                    .map((rep, idx) => (
                      <tr key={`tech-act-${rep.id}-${idx}`} className="hover:bg-slate-50 transition-colors">
                        <td className="py-4 px-3 text-center font-bold text-slate-500">{idx + 1}</td>
                        <td className="py-4 px-4 font-bold text-slate-900 uppercase text-[11px]">{rep.siteName}</td>
                        <td className="py-4 px-4 text-slate-800 font-semibold uppercase text-[11px] leading-relaxed">{rep.clientRequest}</td>
                        <td className="py-4 px-3 font-bold text-slate-800 text-[11px]">{rep.proformaNo}</td>
                        <td className="py-4 px-3 font-bold text-slate-800 text-[11px]">{rep.machineType}</td>
                        <td className="py-4 px-4 font-bold text-slate-800 text-[11px]">{rep.technicianName}</td>
                        <td className="py-4 px-4 leading-relaxed text-[11px]">
                          {rep.checkingResult.split('\n').map((l, i) => (
                            <div key={i} className={l.startsWith('REG NO #') ? 'font-extrabold text-blue-800' : 'text-slate-700'}>{l}</div>
                          ))}
                        </td>
                        <td className="py-4 px-4 text-slate-600 whitespace-nowrap text-[11px] font-mono">{rep.createdAt}</td>
                        <td className="py-4 px-4 text-center">
                          <button
                            onClick={() => handleForwardSurveyReport(rep)}
                            className="bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 shadow-xs mx-auto cursor-pointer"
                          >
                            Forward <SendHorizontal className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ==================== MODULE 3: TECHNICAL REPORT MODULE ==================== */}
      {activeTab === 'technical_report' && (
        <div className="bg-slate-50/50 p-6 rounded-3xl border border-slate-200 shadow-xs space-y-6">
          {/* Sub-Tabs: Active Technical Report vs Approved Technical Report */}
          <div className="flex items-center gap-2 border-b border-slate-200 pb-3">
            <button
              onClick={() => setTechnicalReportSubTab('active')}
              className={cn(
                "px-5 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer border",
                technicalReportSubTab === 'active'
                  ? "bg-white text-purple-900 border-slate-200 shadow-xs"
                  : "bg-slate-100 text-slate-600 border-transparent hover:bg-white"
              )}
            >
              Active Technical Report
            </button>
            <button
              onClick={() => setTechnicalReportSubTab('approved')}
              className={cn(
                "px-5 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer border",
                technicalReportSubTab === 'approved'
                  ? "bg-white text-purple-900 border-slate-200 shadow-xs"
                  : "bg-slate-100 text-slate-600 border-transparent hover:bg-white"
              )}
            >
              Approved Technical Report
            </button>
          </div>

          {/* ACTIVE TECHNICAL REPORT TAB (Matching Image 2) */}
          {technicalReportSubTab === 'active' && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex flex-col md:flex-row justify-between md:items-center gap-4 bg-slate-50/30">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                  <span>Show</span>
                  <select className="border border-slate-200 bg-white rounded-lg px-2 py-1 focus:outline-none">
                    <option>10</option>
                    <option>25</option>
                  </select>
                  <span>entries</span>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="border border-slate-200 bg-white rounded-lg pl-3 pr-8 py-1.5 text-xs focus:outline-none"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-100/90 text-slate-700 font-bold border-b border-slate-200">
                      <th className="py-3 px-3 w-10 text-center">S/N <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[140px]">Site Name <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[220px]">Client Request <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-3 min-w-[100px]">Proforma No <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-3 min-w-[110px]">Machine Type <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[130px]">Technician Name <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[280px]">Checking Result <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[120px]">Created At <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[100px] text-center">Actions <span className="text-slate-400 font-normal">↓↑</span></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                    {technicalReports
                      .filter(r => r.status === 'active')
                      .filter(r => 
                        r.siteName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        r.clientRequest.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map((report, index) => (
                        <tr key={`tech-rep-active-${report.id}-${index}`} className="hover:bg-slate-50 transition-colors">
                          <td className="py-4 px-3 text-center font-bold text-slate-500">{index + 1}</td>
                          <td className="py-4 px-4 font-bold text-slate-900 uppercase text-[11px]">{report.siteName}</td>
                          <td className="py-4 px-4 text-slate-800 font-semibold uppercase text-[11px] leading-relaxed">{report.clientRequest}</td>
                          <td className="py-4 px-3 font-bold text-slate-800 text-[11px]">{report.proformaNo}</td>
                          <td className="py-4 px-3 font-bold text-slate-800 text-[11px]">{report.machineType}</td>
                          <td className="py-4 px-4 font-bold text-slate-800 uppercase text-[11px]">{report.technicianName}</td>
                          <td className="py-4 px-4 leading-relaxed text-[11px]">
                            {report.checkingResult.split('\n').map((line, lIdx) => (
                              <div key={lIdx} className={line.startsWith('REG NO #') ? 'font-extrabold text-blue-800 mb-1' : 'text-slate-700'}>
                                {line}
                              </div>
                            ))}
                          </td>
                          <td className="py-4 px-4 text-slate-600 whitespace-nowrap text-[11px] font-mono">{report.createdAt}</td>
                          <td className="py-4 px-4 text-center relative">
                            <button
                              onClick={() => setActiveDropdownId(activeDropdownId === report.id ? null : report.id)}
                              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold text-xs px-3 py-1.5 rounded-lg flex items-center justify-center gap-1 shadow-sm mx-auto cursor-pointer transition-all"
                            >
                              Action <ChevronDown className="w-3 h-3" />
                            </button>

                            {/* Dropdown Action Menu */}
                            {activeDropdownId === report.id && (
                              <div className="absolute right-4 top-12 z-50 w-44 bg-white rounded-xl shadow-xl border border-slate-200 py-1 text-left text-xs font-medium">
                                <button
                                  onClick={() => { setEditingTechReport(report); setActiveDropdownId(null); }}
                                  className="w-full px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-slate-700"
                                >
                                  <Edit3 className="w-3.5 h-3.5 text-orange-600" /> Edit Report
                                </button>
                                <button
                                  onClick={() => { setViewingTechReport(report); setActiveDropdownId(null); }}
                                  className="w-full px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-purple-700 font-bold"
                                >
                                  <Eye className="w-3.5 h-3.5 text-purple-600" /> View Tech Report
                                </button>
                                <button
                                  onClick={() => { setViewingProforma(report); setActiveDropdownId(null); }}
                                  className="w-full px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-slate-800 font-bold"
                                >
                                  <FileText className="w-3.5 h-3.5 text-slate-800" /> Preview Proforma
                                </button>
                                <button
                                  onClick={() => handleApproveTechnicalReport(report)}
                                  className="w-full px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-emerald-700 font-bold border-t border-slate-100"
                                >
                                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Approve Report
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    {technicalReports.filter(r => r.status === 'active').length === 0 && (
                      <tr>
                        <td colSpan={9} className="py-8 text-center text-slate-400 font-bold">
                          No active technical reports found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* APPROVED TECHNICAL REPORT TAB (Matching Image 1) */}
          {technicalReportSubTab === 'approved' && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex flex-col md:flex-row justify-between md:items-center gap-4 bg-slate-50/30">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                  <span>Show</span>
                  <select className="border border-slate-200 bg-white rounded-lg px-2 py-1 focus:outline-none">
                    <option>10</option>
                    <option>25</option>
                  </select>
                  <span>entries</span>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="border border-slate-200 bg-white rounded-lg pl-3 pr-8 py-1.5 text-xs focus:outline-none"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-100/90 text-slate-700 font-bold border-b border-slate-200">
                      <th className="py-3 px-3 w-10 text-center">S/N <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[140px]">Site Name <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[220px]">Client Request <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-3 min-w-[100px]">Proforma No <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-3 min-w-[120px]">Machine Type <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[130px]">Technician Name <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[280px]">Checking Result <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[130px]">Approved At <span className="text-slate-400 font-normal">↓↑</span></th>
                      <th className="py-3 px-4 min-w-[120px] text-center">Actions <span className="text-slate-400 font-normal">↓↑</span></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                    {technicalReports
                      .filter(r => r.status === 'approved')
                      .filter(r => 
                        r.siteName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        r.clientRequest.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map((report, index) => (
                        <tr key={`tech-rep-approved-${report.id}-${index}`} className="hover:bg-slate-50 transition-colors">
                          <td className="py-4 px-3 text-center font-bold text-slate-500">{index + 1}</td>
                          <td className="py-4 px-4 font-bold text-slate-900 uppercase text-[11px]">{report.siteName}</td>
                          <td className="py-4 px-4 text-slate-800 font-semibold uppercase text-[11px] leading-relaxed">{report.clientRequest}</td>
                          <td className="py-4 px-3 font-bold text-slate-800 text-[11px]">{report.proformaNo}</td>
                          <td className="py-4 px-3 font-bold text-slate-800 uppercase text-[11px]">{report.machineType}</td>
                          <td className="py-4 px-4 font-bold text-slate-800 uppercase text-[11px]">{report.technicianName}</td>
                          <td className="py-4 px-4 leading-relaxed text-[11px]">
                            {report.checkingResult.split('\n').map((line, lIdx) => (
                              <div key={lIdx} className={line.startsWith('REG NO #') ? 'font-extrabold text-blue-800 mb-1' : 'text-slate-700'}>
                                {line}
                              </div>
                            ))}
                          </td>
                          <td className="py-4 px-4 text-slate-600 whitespace-nowrap text-[11px] font-mono">{report.approvedAt || report.createdAt}</td>
                          <td className="py-4 px-4 text-center">
                            <div className="flex flex-col gap-1 items-center justify-center">
                              <button
                                onClick={() => setViewingTechReport(report)}
                                className="bg-purple-600 hover:bg-purple-700 text-white text-[11px] font-semibold px-2.5 py-1 rounded-md shadow-xs transition-all w-full max-w-[80px] cursor-pointer"
                              >
                                Tech
                              </button>
                              <button
                                onClick={() => setViewingProforma(report)}
                                className="bg-slate-800 hover:bg-slate-900 text-white text-[11px] font-semibold px-2.5 py-1 rounded-md shadow-xs transition-all w-full max-w-[80px] cursor-pointer"
                              >
                                Proforma
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    {technicalReports.filter(r => r.status === 'approved').length === 0 && (
                      <tr>
                        <td colSpan={9} className="py-8 text-center text-slate-400 font-bold">
                          No approved technical reports found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==================== MODULE 4: TECHNICIANS MODULE ==================== */}
      {activeTab === 'technicians' && (
        <div className="bg-slate-50/50 p-6 rounded-3xl border border-slate-200 shadow-xs space-y-6">
          <div className="flex justify-between items-center border-b border-slate-200 pb-4">
            <div>
              <h2 className="text-xl font-black text-slate-900 tracking-tight">Technicians Task Responses</h2>
              <p className="text-xs text-slate-500">View field updates, site inspection results, and progressive task statuses sent back by field technicians</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tasks.map((task, index) => (
              <div key={`tech-grid-task-${task.id}-${index}`} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between space-y-4 hover:border-purple-300 transition-all">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded bg-purple-100 text-purple-800">
                      {task.status || 'In Progress'}
                    </span>
                    <span className="text-[11px] font-mono text-slate-400">
                      {task.createdAt?.toDate?.()?.toLocaleDateString() || 'Recent'}
                    </span>
                  </div>

                  <h3 className="font-bold text-slate-900 text-sm">{task.title}</h3>
                  <p className="text-xs text-slate-600 line-clamp-3">{task.description}</p>

                  <div className="pt-2 text-xs space-y-1 text-slate-500 font-medium">
                    <div className="flex items-center gap-1.5"><UserIcon className="w-3.5 h-3.5 text-purple-600" /> Client: {task.clientName || 'General Client'}</div>
                    <div className="flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5 text-purple-600" /> Tech: {task.technicianName || (task as any).assignedTechnicianId || 'Assigned Technician'}</div>
                  </div>
                </div>

                <div className="flex gap-2 pt-2 border-t border-slate-100">
                  <button
                    onClick={() => setViewingTechnicianResponse(task)}
                    className="flex-1 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl text-xs font-bold transition-all cursor-pointer text-center"
                  >
                    View Response
                  </button>
                  <button
                    onClick={() => {
                      const clientName = task.clientName || 'Client';
                      const taskTitle = task.title || 'Task';
                      const autoClientRequest = task.clientName ? `${task.clientName} - ${taskTitle}` : taskTitle;

                      setCreateSurveyFormData(prev => ({
                        ...prev,
                        clientRequest: autoClientRequest,
                        siteName: task.clientName || 'Branch Site',
                        technicianName: task.technicianName || profile?.displayName || 'MICHAEL CHUWA',
                        checkingResults: task.description || ''
                      }));
                      setActiveTab('site_survey');
                      setIsCreateSurveyModalOpen(true);
                    }}
                    className="flex-1 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold transition-all cursor-pointer text-center"
                  >
                    Create Survey
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ==================== TASKS MODULE (Existing) ==================== */}
      {activeTab === 'tasks' && (
        <div className="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm space-y-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search technical tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-purple-900 transition-all font-medium text-sm"
              />
            </div>
            <div className="flex items-center gap-1.5 p-1.5 bg-slate-50 rounded-2xl border border-slate-100">
              {['all', 'pending', 'in-progress', 'pending review', 'completed'].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={cn(
                    "px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer",
                    statusFilter === status 
                      ? "bg-purple-900 text-white shadow-sm" 
                      : "text-slate-500 hover:bg-white hover:text-purple-900"
                  )}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            {filteredTasks.map((task, index) => (
              <motion.div
                key={`task-filtered-${task.id}-${index}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="group bg-slate-50 p-6 rounded-3xl border border-transparent hover:border-purple-900/10 hover:bg-white hover:shadow-xl transition-all flex flex-col md:flex-row md:items-center justify-between gap-6"
              >
                <div className="flex items-start gap-5 flex-1">
                  <div className={cn(
                    "w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm transition-colors",
                    task.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                    task.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                    'bg-orange-100 text-orange-700'
                  )}>
                    {task.status === 'completed' ? <CheckCircle2 className="w-7 h-7" /> : <Clock className="w-7 h-7" />}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-black text-slate-900 truncate">{task.title}</h3>
                      <span className="px-2 py-0.5 bg-purple-900 text-white text-[8px] font-black uppercase tracking-widest rounded-md">Technical</span>
                    </div>
                    <p className="text-sm text-slate-500 font-medium mb-3 line-clamp-2">{task.description}</p>
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-400">
                        <UserIcon className="w-3 h-3" />
                        {task.clientName}
                      </div>
                      <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-400">
                        <Clock className="w-3 h-3" />
                        {task.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button 
                    onClick={() => handleStatusChange(task.id, 'in-progress')}
                    className="px-4 py-2 bg-blue-900 text-white rounded-xl text-xs font-bold hover:bg-blue-800 transition-all"
                  >
                    Start Task
                  </button>
                  <button 
                    onClick={() => {
                      setSelectedTaskForReport(task);
                      setIsReportModalOpen(true);
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-xl text-xs font-bold hover:bg-purple-700 transition-all flex items-center gap-1.5"
                  >
                    <FileText className="w-4 h-4" /> Submit Report
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* ==================== MODULE 6: JOB CARDS MODULE ==================== */}
      {activeTab === 'jobcards' && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-xs overflow-hidden">
            {/* Card Header */}
            <div className="p-5 border-b border-slate-100 bg-white flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-xl font-bold text-slate-800 tracking-tight">Manage Job Card Request</h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsRequestJobCardModalOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-4 py-2 rounded-xl flex items-center gap-1.5 shadow-xs cursor-pointer transition-all"
                >
                  <Plus className="w-4 h-4" /> Request Job Card
                </button>
                <button
                  onClick={() => setIsNewJobCardModalOpen(true)}
                  className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold px-4 py-2 rounded-xl flex items-center gap-1.5 shadow-xs cursor-pointer transition-all"
                >
                  <Plus className="w-4 h-4" /> Create New Job Card
                </button>
              </div>
            </div>

            <div className="p-5 space-y-4">
              {/* Controls: Show Entries & Search */}
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-slate-600 font-medium">
                <div className="flex items-center gap-2">
                  <span>Show</span>
                  <select
                    value={entriesPerPage}
                    onChange={(e) => {
                      setEntriesPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="border border-slate-300 rounded px-2.5 py-1 bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer font-bold"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span>entries</span>
                </div>

                <div className="flex items-center gap-2">
                  <span>Search</span>
                  <input
                    type="text"
                    value={jobCardSearchTerm}
                    onChange={(e) => {
                      setJobCardSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    placeholder=""
                    className="border border-slate-200 rounded-lg px-3 py-1.5 bg-slate-50 text-slate-800 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 w-48 sm:w-64 font-medium"
                  />
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto border border-slate-200/80 rounded-lg">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-100 text-slate-800 font-extrabold border-b border-slate-200 select-none">
                      <th 
                        onClick={() => handleSort('sn')} 
                        className="py-3 px-4 cursor-pointer hover:bg-slate-200/70 transition-colors border-r border-slate-200/80 w-16"
                      >
                        <div className="flex items-center justify-between gap-1">
                          <span>S/N</span>
                          <span className="text-[10px] text-slate-400 font-normal">↑↓</span>
                        </div>
                      </th>
                      <th 
                        onClick={() => handleSort('siteName')} 
                        className="py-3 px-4 cursor-pointer hover:bg-slate-200/70 transition-colors border-r border-slate-200/80 min-w-[220px]"
                      >
                        <div className="flex items-center justify-between gap-1">
                          <span>Site Name</span>
                          <span className="text-[10px] text-slate-400 font-normal">↑↓</span>
                        </div>
                      </th>
                      <th 
                        onClick={() => handleSort('clientRequest')} 
                        className="py-3 px-4 cursor-pointer hover:bg-slate-200/70 transition-colors border-r border-slate-200/80 min-w-[380px]"
                      >
                        <div className="flex items-center justify-between gap-1">
                          <span>Client Request</span>
                          <span className="text-[10px] text-slate-400 font-normal">↑↓</span>
                        </div>
                      </th>
                      <th 
                        onClick={() => handleSort('jobCardNo')} 
                        className="py-3 px-4 cursor-pointer hover:bg-slate-200/70 transition-colors border-r border-slate-200/80 whitespace-nowrap w-32"
                      >
                        <div className="flex items-center justify-between gap-1">
                          <span>Job Card No</span>
                          <span className="text-[10px] text-slate-400 font-normal">↑↓</span>
                        </div>
                      </th>
                      <th 
                        onClick={() => handleSort('status')} 
                        className="py-3 px-4 cursor-pointer hover:bg-slate-200/70 transition-colors text-right whitespace-nowrap w-28"
                      >
                        <div className="flex items-center justify-end gap-1">
                          <span>Actions</span>
                          <span className="text-[10px] text-slate-400 font-normal">↑↓</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {paginatedJobCards.map((item, index) => (
                      <tr key={`jc-${item.id}-${index}`} className="hover:bg-slate-50/90 transition-colors">
                        <td className="py-3.5 px-4 font-normal text-slate-800 border-r border-slate-100/80">{item.sn}</td>
                        <td className="py-3.5 px-4 font-extrabold text-slate-900 border-r border-slate-100/80">{item.siteName}</td>
                        <td className="py-3.5 px-4 text-slate-800 font-medium leading-relaxed border-r border-slate-100/80">{item.clientRequest}</td>
                        <td className="py-3.5 px-4 font-mono font-bold text-slate-800 border-r border-slate-100/80 whitespace-nowrap">{item.jobCardNo}</td>
                        <td className="py-3.5 px-4 text-right whitespace-nowrap font-extrabold text-slate-900">
                          <span className="cursor-pointer hover:text-blue-900 transition-colors">
                            {item.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {paginatedJobCards.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-slate-400 font-bold italic">
                          No matching Job Card requests found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination footer */}
              <div className="flex flex-col sm:flex-row justify-between items-center gap-2 pt-2 text-xs text-slate-500 font-medium">
                <div>
                  Showing {totalEntries > 0 ? (currentPage - 1) * entriesPerPage + 1 : 0} to {Math.min(currentPage * entriesPerPage, totalEntries)} of {totalEntries} entries
                </div>
                {totalEntries > entriesPerPage && (
                  <div className="flex gap-1">
                    <button
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      className="px-3 py-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40 cursor-pointer font-semibold"
                    >
                      Previous
                    </button>
                    <button
                      disabled={currentPage * entriesPerPage >= totalEntries}
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      className="px-3 py-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40 cursor-pointer font-semibold"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== MODULE 7: REPORT REVIEW MODULE ==================== */}
      {activeTab === 'reports' && (
        <div className="bg-slate-50/50 p-6 rounded-3xl border border-slate-200 shadow-xs space-y-6">
          <div className="flex justify-between items-center border-b border-slate-200 pb-4">
            <div>
              <h2 className="text-xl font-black text-slate-900 tracking-tight">Technical Reports Review</h2>
              <p className="text-xs text-slate-500">HOD and Manager technical verification and report approval console</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/30">
              <span className="text-xs font-bold text-slate-700">Submitted Technical Reports ({technicalReports.length})</span>
              <input
                type="text"
                placeholder="Filter submitted reports..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="border border-slate-200 bg-white rounded-lg px-3 py-1.5 text-xs focus:outline-none"
              />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-100/90 text-slate-700 font-bold border-b border-slate-200">
                    <th className="py-3 px-3 w-10 text-center">#</th>
                    <th className="py-3 px-4 min-w-[140px]">Site / Client</th>
                    <th className="py-3 px-4 min-w-[200px]">Inspection Findings</th>
                    <th className="py-3 px-3 min-w-[120px]">Technician</th>
                    <th className="py-3 px-3 min-w-[100px]">Status</th>
                    <th className="py-3 px-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                  {technicalReports.map((report, idx) => (
                    <tr key={`tech-review-${report.id}-${idx}`} className="hover:bg-slate-50 transition-colors">
                      <td className="py-4 px-3 text-center font-bold text-slate-500">{idx + 1}</td>
                      <td className="py-4 px-4 font-bold text-slate-900 uppercase text-[11px]">{report.siteName}</td>
                      <td className="py-4 px-4 text-slate-700 text-[11px] leading-relaxed line-clamp-2">{report.clientRequest}</td>
                      <td className="py-4 px-3 font-bold text-slate-800 text-[11px]">{report.technicianName}</td>
                      <td className="py-4 px-3">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-purple-100 text-purple-800">
                          {report.status}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => setViewingTechReport(report)}
                            className="px-2.5 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-[11px] font-bold cursor-pointer transition-all"
                          >
                            Review
                          </button>
                          <button
                            onClick={() => handleForwardSurveyReport(report)}
                            className="px-2.5 py-1 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-[11px] font-bold cursor-pointer transition-all flex items-center gap-1"
                          >
                            Forward <SendHorizontal className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ==================== CREATE SURVEY MODAL (Matching Image 4) ==================== */}
      <AnimatePresence>
        {isCreateSurveyModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsCreateSurveyModalOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col z-10"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-white">
                <h2 className="text-xl font-bold text-slate-900 tracking-tight">Create Survey</h2>
                <button
                  onClick={() => setIsCreateSurveyModalOpen(false)}
                  className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Body */}
              <form onSubmit={handleCreateSurveySubmit} className="p-6 overflow-y-auto space-y-5 text-xs font-medium text-slate-700 flex-1">
                {/* Row 1: Client Request, Name of Technician, Type of work */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label className="font-semibold text-slate-700">Client Request</label>
                      {tasks.length > 0 && (
                        <select
                          onChange={e => {
                            const selectedT = tasks.find(t => t.id === e.target.value);
                            if (selectedT) {
                              const clientName = selectedT.clientName || 'Client';
                              const autoReq = selectedT.clientName ? `${selectedT.clientName} - ${selectedT.title}` : selectedT.title;
                              setCreateSurveyFormData(prev => ({
                                ...prev,
                                clientRequest: autoReq,
                                siteName: selectedT.clientName || prev.siteName || 'Branch Site',
                                technicianName: selectedT.technicianName || prev.technicianName,
                                checkingResults: selectedT.description || prev.checkingResults
                              }));
                            }
                          }}
                          defaultValue=""
                          className="text-[10px] text-purple-700 bg-purple-50 hover:bg-purple-100 px-2 py-0.5 rounded border border-purple-200 cursor-pointer focus:outline-none"
                        >
                          <option value="" disabled>Autofill from Task...</option>
                          {tasks.map(t => (
                            <option key={t.id} value={t.id}>
                              {t.clientName ? `${t.clientName}: ${t.title}` : t.title}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <input
                      type="text"
                      value={createSurveyFormData.clientRequest}
                      onChange={e => setCreateSurveyFormData({ ...createSurveyFormData, clientRequest: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-purple-600 focus:outline-none text-xs font-medium text-slate-800"
                      placeholder="e.g. NBC TARIME BRANCH - ATM & Server Room AC Maintenance"
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="font-semibold text-slate-700">Name of Technician</label>
                    <input
                      type="text"
                      value={createSurveyFormData.technicianName}
                      onChange={e => setCreateSurveyFormData({ ...createSurveyFormData, technicianName: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-purple-600 focus:outline-none"
                      placeholder="e.g. MICHAEL CHUWA"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="font-semibold text-slate-700">Type of work</label>
                    <input
                      type="text"
                      value={createSurveyFormData.typeOfWork}
                      onChange={e => setCreateSurveyFormData({ ...createSurveyFormData, typeOfWork: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-purple-600 focus:outline-none"
                      placeholder="e.g. Inspection & Repair"
                    />
                  </div>
                </div>

                {/* Row 2: Type of Machine/Equipment, Quantity, User Department */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <label className="font-semibold text-slate-700">Type of Machine/Equipment</label>
                    <input
                      type="text"
                      value={createSurveyFormData.machineType}
                      onChange={e => setCreateSurveyFormData({ ...createSurveyFormData, machineType: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-purple-600 focus:outline-none"
                      placeholder="e.g. AIR CONDITIONERS"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="font-semibold text-slate-700">Quantity</label>
                    <input
                      type="text"
                      value={createSurveyFormData.quantity}
                      onChange={e => setCreateSurveyFormData({ ...createSurveyFormData, quantity: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-purple-600 focus:outline-none"
                      placeholder="e.g. 2"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="font-semibold text-slate-700">User Department</label>
                    <input
                      type="text"
                      value={createSurveyFormData.userDepartment}
                      onChange={e => setCreateSurveyFormData({ ...createSurveyFormData, userDepartment: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-purple-600 focus:outline-none"
                      placeholder="e.g. Branch Operations"
                    />
                  </div>
                </div>

                {/* Checking Results with Rich Text Toolbar */}
                <div className="space-y-1.5">
                  <label className="font-semibold text-slate-700">Checking Results</label>
                  <div className="border border-slate-300 rounded-lg overflow-hidden bg-white">
                    {/* Toolbar */}
                    <div className="flex flex-wrap items-center gap-2 p-2 bg-slate-50 border-b border-slate-200 text-slate-600 text-xs">
                      <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                      <Bold className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900" />
                      <Italic className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900" />
                      <Underline className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900" />
                      <span className="text-[11px] font-semibold border-l pl-2 border-slate-300">Poppins ▾</span>
                      <span className="font-bold text-yellow-600 bg-yellow-100 px-1 rounded text-[10px]">A ▾</span>
                      <List className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900 ml-1" />
                      <ListOrdered className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900" />
                      <TableIcon className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900" />
                      <Link className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900" />
                      <ImageIcon className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900" />
                      <Code className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900" />
                      <Maximize2 className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900 ml-auto" />
                      <HelpCircle className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900" />
                    </div>
                    <textarea
                      rows={4}
                      value={createSurveyFormData.checkingResults}
                      onChange={e => setCreateSurveyFormData({ ...createSurveyFormData, checkingResults: e.target.value })}
                      className="w-full p-3 focus:outline-none resize-none font-sans text-xs text-slate-800"
                      placeholder="Enter detailed checking results, machine conditions, defect descriptions..."
                    />
                  </div>
                </div>

                {/* Remedial Recommendation with Rich Text Toolbar */}
                <div className="space-y-1.5">
                  <label className="font-semibold text-slate-700">Remedial Recommendation</label>
                  <div className="border border-slate-300 rounded-lg overflow-hidden bg-white">
                    {/* Toolbar */}
                    <div className="flex flex-wrap items-center gap-2 p-2 bg-slate-50 border-b border-slate-200 text-slate-600 text-xs">
                      <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                      <Bold className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900" />
                      <Italic className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900" />
                      <Underline className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900" />
                      <span className="text-[11px] font-semibold border-l pl-2 border-slate-300">Poppins ▾</span>
                      <span className="font-bold text-yellow-600 bg-yellow-100 px-1 rounded text-[10px]">A ▾</span>
                      <List className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900 ml-1" />
                      <ListOrdered className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900" />
                      <TableIcon className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900" />
                      <Link className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900" />
                      <ImageIcon className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900" />
                      <Code className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900" />
                      <Maximize2 className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900 ml-auto" />
                      <HelpCircle className="w-3.5 h-3.5 cursor-pointer hover:text-slate-900" />
                    </div>
                    <textarea
                      rows={3}
                      value={createSurveyFormData.remedialRecommendation}
                      onChange={e => setCreateSurveyFormData({ ...createSurveyFormData, remedialRecommendation: e.target.value })}
                      className="w-full p-3 focus:outline-none resize-none font-sans text-xs text-slate-800"
                      placeholder="Enter technical recommendations, replacement units, required spare parts..."
                    />
                  </div>
                </div>

                {/* Footer Buttons */}
                <div className="flex justify-end items-center gap-3 pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setIsCreateSurveyModalOpen(false)}
                    className="px-5 py-2 border border-purple-300 text-purple-700 font-semibold rounded-lg hover:bg-purple-50 transition-all cursor-pointer flex items-center gap-1"
                  >
                    <X className="w-4 h-4" /> Close
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg shadow-md transition-all cursor-pointer flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" /> Submit
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ==================== VIEW TECHNICAL SURVEY REPORT MODAL ==================== */}
      <AnimatePresence>
        {(viewingSurvey || viewingTechReport) && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => { setViewingSurvey(null); setViewingTechReport(null); }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col z-20"
            >
              <div className="p-4 bg-purple-900 text-white flex justify-between items-center">
                <h3 className="font-bold text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Technical Survey Report Details
                </h3>
                <button onClick={() => { setViewingSurvey(null); setViewingTechReport(null); }} className="hover:opacity-80">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto space-y-6 text-xs text-slate-800 font-sans">
                {/* Document Header */}
                <div className="border-b-2 border-slate-900 pb-4 flex justify-between items-start">
                  <div>
                    <h1 className="text-xl font-black text-slate-900 tracking-tight">TECHNICAL SURVEY REPORT</h1>
                    <p className="text-xs font-bold text-purple-800">REF: {(viewingSurvey || viewingTechReport)?.siteName}</p>
                  </div>
                  <div className="text-right text-[11px] font-mono text-slate-500">
                    <div>Date: {(viewingSurvey || viewingTechReport)?.createdAt}</div>
                    <div>Status: {(viewingSurvey || viewingTechReport)?.status}</div>
                  </div>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <div>
                    <span className="font-bold text-slate-500 block text-[10px] uppercase">Site / Branch Name</span>
                    <span className="font-extrabold text-slate-900 text-sm">{(viewingSurvey || viewingTechReport)?.siteName}</span>
                  </div>
                  <div>
                    <span className="font-bold text-slate-500 block text-[10px] uppercase">Machine / Equipment Type</span>
                    <span className="font-extrabold text-slate-900 text-sm">{(viewingSurvey || viewingTechReport)?.machineType}</span>
                  </div>
                  <div>
                    <span className="font-bold text-slate-500 block text-[10px] uppercase">Assigned Technician</span>
                    <span className="font-bold text-slate-800">{(viewingSurvey || viewingTechReport)?.technicianName}</span>
                  </div>
                  <div>
                    <span className="font-bold text-slate-500 block text-[10px] uppercase">Proforma Ref #</span>
                    <span className="font-bold text-purple-700">{(viewingSurvey || viewingTechReport)?.proformaNo || '26604'}</span>
                  </div>
                </div>

                {/* Client Request */}
                <div className="space-y-1">
                  <h4 className="font-bold text-slate-900 uppercase text-[10px] tracking-wider text-purple-900">Client Request / Scope</h4>
                  <p className="p-3 bg-purple-50/50 rounded-lg border border-purple-100 font-medium text-slate-800 leading-relaxed">
                    {(viewingSurvey || viewingTechReport)?.clientRequest}
                  </p>
                </div>

                {/* Checking Results */}
                <div className="space-y-1">
                  <h4 className="font-bold text-slate-900 uppercase text-[10px] tracking-wider text-purple-900">Technical Checking Results</h4>
                  <div className="p-4 bg-white rounded-lg border border-slate-200 font-mono text-slate-800 leading-relaxed whitespace-pre-wrap">
                    {(viewingSurvey || viewingTechReport)?.checkingResult}
                  </div>
                </div>

                {/* Remedial Recommendations */}
                <div className="space-y-1">
                  <h4 className="font-bold text-slate-900 uppercase text-[10px] tracking-wider text-purple-900">Remedial Recommendations</h4>
                  <div className="p-4 bg-emerald-50/40 rounded-lg border border-emerald-100 font-medium text-slate-800 leading-relaxed">
                    {(viewingSurvey || viewingTechReport)?.remedialRecommendation || 'Complete equipment overhaul and replacement recommended.'}
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-slate-100 flex justify-between items-center bg-slate-50">
                <button
                  onClick={() => window.print()}
                  className="px-4 py-2 bg-slate-800 text-white rounded-lg font-bold text-xs flex items-center gap-1.5"
                >
                  <Printer className="w-4 h-4" /> Print Report
                </button>
                <button
                  onClick={() => { setViewingSurvey(null); setViewingTechReport(null); }}
                  className="px-5 py-2 border border-slate-300 rounded-lg font-bold text-xs text-slate-700 hover:bg-slate-100"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ==================== PREVIEW PROFORMA INVOICE MODAL ==================== */}
      <AnimatePresence>
        {viewingProforma && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setViewingProforma(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col z-20"
            >
              <div className="p-4 bg-slate-900 text-white flex justify-between items-center">
                <h3 className="font-bold text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4 text-purple-400" /> Attached Proforma Invoice Preview
                </h3>
                <button onClick={() => setViewingProforma(null)} className="hover:opacity-80">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto space-y-6 text-xs text-slate-800 font-sans">
                {/* Proforma Header */}
                <div className="flex justify-between items-start border-b pb-4 border-slate-200">
                  <div>
                    <h1 className="text-2xl font-black text-slate-900">PROFORMA INVOICE</h1>
                    <p className="text-xs font-bold text-purple-700">PROFORMA NO # {viewingProforma.proformaNo}</p>
                  </div>
                  <div className="text-right text-xs">
                    <div className="font-bold text-slate-900">{viewingProforma.siteName}</div>
                    <div className="text-slate-500">Date: {viewingProforma.createdAt}</div>
                  </div>
                </div>

                {/* Items Table */}
                <table className="w-full text-left text-xs border border-slate-200">
                  <thead>
                    <tr className="bg-slate-100 font-bold border-b border-slate-200">
                      <th className="p-2 border-r">Item Description</th>
                      <th className="p-2 border-r text-center w-16">Qty</th>
                      <th className="p-2 border-r text-right w-24">Rate (TZS)</th>
                      <th className="p-2 text-right w-28">Amount (TZS)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <tr>
                      <td className="p-3 font-semibold">{viewingProforma.clientRequest}</td>
                      <td className="p-3 text-center font-bold">1</td>
                      <td className="p-3 text-right">1,850,000</td>
                      <td className="p-3 text-right font-bold">1,850,000</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-semibold">Technical Installation & Labor Charges</td>
                      <td className="p-3 text-center font-bold">1</td>
                      <td className="p-3 text-right">350,000</td>
                      <td className="p-3 text-right font-bold">350,000</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50 font-bold">
                      <td colSpan={3} className="p-2 text-right border-t">Subtotal:</td>
                      <td className="p-2 text-right border-t">2,200,000</td>
                    </tr>
                    <tr className="bg-slate-50 font-bold">
                      <td colSpan={3} className="p-2 text-right">VAT (18%):</td>
                      <td className="p-2 text-right">396,000</td>
                    </tr>
                    <tr className="bg-purple-900 text-white font-black text-sm">
                      <td colSpan={3} className="p-3 text-right">TOTAL AMOUNT:</td>
                      <td className="p-3 text-right">TZS 2,596,000</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="p-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50">
                <button
                  onClick={() => setViewingProforma(null)}
                  className="px-5 py-2 border border-slate-300 rounded-lg font-bold text-xs text-slate-700 hover:bg-slate-100"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ==================== EDIT TECHNICAL REPORT MODAL ==================== */}
      <AnimatePresence>
        {editingTechReport && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setEditingTechReport(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto z-20 text-xs font-medium"
            >
              <div className="flex justify-between items-center border-b pb-3">
                <h3 className="font-bold text-lg text-slate-900">Edit Technical Survey Report</h3>
                <button onClick={() => setEditingTechReport(null)}><X className="w-5 h-5 text-slate-400" /></button>
              </div>

              <form onSubmit={handleSaveEditTechReport} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-slate-700">Site Name</label>
                    <input
                      type="text"
                      value={editingTechReport.siteName}
                      onChange={e => setEditingTechReport({ ...editingTechReport, siteName: e.target.value })}
                      className="w-full p-2 border rounded-lg focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-slate-700">Proforma No</label>
                    <input
                      type="text"
                      value={editingTechReport.proformaNo}
                      onChange={e => setEditingTechReport({ ...editingTechReport, proformaNo: e.target.value })}
                      className="w-full p-2 border rounded-lg focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="font-semibold text-slate-700">Client Request</label>
                  <textarea
                    rows={2}
                    value={editingTechReport.clientRequest}
                    onChange={e => setEditingTechReport({ ...editingTechReport, clientRequest: e.target.value })}
                    className="w-full p-2 border rounded-lg focus:outline-none"
                  />
                </div>

                <div>
                  <label className="font-semibold text-slate-700">Checking Results</label>
                  <textarea
                    rows={4}
                    value={editingTechReport.checkingResult}
                    onChange={e => setEditingTechReport({ ...editingTechReport, checkingResult: e.target.value })}
                    className="w-full p-2 border rounded-lg focus:outline-none font-mono text-xs"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-3 border-t">
                  <button type="button" onClick={() => setEditingTechReport(null)} className="px-4 py-2 border rounded-lg">Cancel</button>
                  <button type="submit" className="px-5 py-2 bg-purple-600 text-white rounded-lg font-bold">Save Changes</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ==================== EDIT SITE SURVEY MODAL ==================== */}
      <AnimatePresence>
        {editingSurvey && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setEditingSurvey(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto z-20 text-xs font-medium"
            >
              <div className="flex justify-between items-center border-b pb-3">
                <h3 className="font-bold text-lg text-slate-900">Edit Site Survey</h3>
                <button onClick={() => setEditingSurvey(null)}><X className="w-5 h-5 text-slate-400" /></button>
              </div>

              <form onSubmit={handleSaveEditSurvey} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-slate-700">Site Name</label>
                    <input
                      type="text"
                      value={editingSurvey.siteName}
                      onChange={e => setEditingSurvey({ ...editingSurvey, siteName: e.target.value })}
                      className="w-full p-2 border rounded-lg focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-slate-700">Technician Name</label>
                    <input
                      type="text"
                      value={editingSurvey.technicianName}
                      onChange={e => setEditingSurvey({ ...editingSurvey, technicianName: e.target.value })}
                      className="w-full p-2 border rounded-lg focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="font-semibold text-slate-700">Client Request</label>
                  <textarea
                    rows={2}
                    value={editingSurvey.clientRequest}
                    onChange={e => setEditingSurvey({ ...editingSurvey, clientRequest: e.target.value })}
                    className="w-full p-2 border rounded-lg focus:outline-none"
                  />
                </div>

                <div>
                  <label className="font-semibold text-slate-700">Checking Results</label>
                  <textarea
                    rows={4}
                    value={editingSurvey.checkingResult}
                    onChange={e => setEditingSurvey({ ...editingSurvey, checkingResult: e.target.value })}
                    className="w-full p-2 border rounded-lg focus:outline-none font-mono text-xs"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-3 border-t">
                  <button type="button" onClick={() => setEditingSurvey(null)} className="px-4 py-2 border rounded-lg">Cancel</button>
                  <button type="submit" className="px-5 py-2 bg-purple-600 text-white rounded-lg font-bold">Save Changes</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ==================== FORWARD SURVEY REPORT MODAL (Select Department) ==================== */}
      <AnimatePresence>
        {forwardingItem && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setForwardingItem(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden z-20 border border-slate-200"
            >
              <div className="bg-purple-900 text-white px-6 py-4 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <SendHorizontal className="w-5 h-5 text-purple-300" />
                  <h3 className="font-bold text-base">Forward Survey Report</h3>
                </div>
                <button onClick={() => setForwardingItem(null)} className="p-1 hover:bg-purple-800 rounded-lg transition-colors cursor-pointer">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleConfirmForward} className="p-6 space-y-4 text-xs font-medium text-slate-700">
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-1.5">
                  <div className="flex justify-between items-center text-[10px] font-bold text-slate-400 uppercase">
                    <span>Site / Branch</span>
                    <span className="text-purple-700 font-mono">Ref: {('proformaNo' in forwardingItem && forwardingItem.proformaNo) || '26604'}</span>
                  </div>
                  <div className="text-sm font-black text-slate-900">{forwardingItem.siteName}</div>
                  <div className="text-slate-600 font-medium line-clamp-2">{forwardingItem.clientRequest}</div>
                  <div className="pt-1 flex items-center gap-4 text-[11px] text-slate-500 border-t border-slate-200/60 mt-1">
                    <div><span className="font-semibold text-slate-700">Tech:</span> {forwardingItem.technicianName}</div>
                    <div><span className="font-semibold text-slate-700">Machine:</span> {forwardingItem.machineType}</div>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="font-bold text-slate-800 flex items-center gap-1.5">
                    <Building2 className="w-4 h-4 text-purple-600" /> Select Destination Department <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedDepartment}
                    onChange={e => setSelectedDepartment(e.target.value)}
                    className="w-full px-3 py-2.5 bg-white border border-slate-300 rounded-xl text-xs font-semibold text-slate-800 focus:ring-2 focus:ring-purple-600 focus:outline-none shadow-xs"
                  >
                    <option value="Billing & Commercial Department">Billing & Commercial Department</option>
                    <option value="Operations & Maintenance Department">Operations & Maintenance Department</option>
                    <option value="Finance & Accounting Department">Finance & Accounting Department</option>
                    <option value="Procurement & Logistics Department">Procurement & Logistics Department</option>
                    <option value="Sales & Client Relations">Sales & Client Relations</option>
                    <option value="Executive / Management Board">Executive / Management Board</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="font-bold text-slate-800">Additional Instructions / Remarks (Optional)</label>
                  <textarea
                    rows={3}
                    value={forwardNotes}
                    onChange={e => setForwardNotes(e.target.value)}
                    placeholder="e.g. Please process proforma invoice for 2x 18000BTU AC units..."
                    className="w-full p-3 bg-white border border-slate-300 rounded-xl text-xs text-slate-800 focus:ring-2 focus:ring-purple-600 focus:outline-none resize-none"
                  />
                </div>

                <div className="flex justify-end items-center gap-3 pt-3 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setForwardingItem(null)}
                    className="px-4 py-2 border border-slate-300 text-slate-700 font-bold rounded-xl hover:bg-slate-50 transition-all cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl shadow-md transition-all cursor-pointer flex items-center gap-1.5"
                  >
                    Forward Report <SendHorizontal className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ==================== VIEW TECHNICIAN RESPONSE MODAL ==================== */}
      <AnimatePresence>
        {viewingTechnicianResponse && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setViewingTechnicianResponse(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col z-20"
            >
              <div className="p-4 bg-purple-900 text-white flex justify-between items-center">
                <h3 className="font-bold text-sm flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-purple-300" /> Technician Field Response Details
                </h3>
                <button onClick={() => setViewingTechnicianResponse(null)} className="hover:opacity-80">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto space-y-4 text-xs text-slate-800">
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-extrabold text-purple-900 text-sm">{viewingTechnicianResponse.title}</span>
                    <span className="px-2.5 py-1 bg-purple-100 text-purple-800 font-bold rounded-lg text-[10px] uppercase">
                      {viewingTechnicianResponse.status || 'In Progress'}
                    </span>
                  </div>
                  <p className="text-slate-600 leading-relaxed">{viewingTechnicianResponse.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4 text-xs font-semibold">
                  <div className="p-3 bg-white border border-slate-200 rounded-lg">
                    <span className="text-slate-400 block text-[10px] uppercase font-bold">Client / Site Name</span>
                    <span className="text-slate-900 font-bold">{viewingTechnicianResponse.clientName || 'General Client'}</span>
                  </div>
                  <div className="p-3 bg-white border border-slate-200 rounded-lg">
                    <span className="text-slate-400 block text-[10px] uppercase font-bold">Assigned Technician</span>
                    <span className="text-slate-900 font-bold">{viewingTechnicianResponse.technicianName || 'Field Technician'}</span>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <h4 className="font-bold text-slate-900 uppercase text-[10px] tracking-wider text-purple-900">Technician Remarks & Observations</h4>
                  <p className="p-4 bg-slate-50 rounded-xl border border-slate-200 leading-relaxed font-sans text-slate-700">
                    {(viewingTechnicianResponse as any).remarks || 'Inspected site on schedule. Found system capacity inadequate for thermal load. Prepared survey findings for HOD technical review.'}
                  </p>
                </div>
              </div>

              <div className="p-4 border-t border-slate-100 flex justify-between items-center bg-slate-50">
                <button
                  onClick={() => {
                    const clientName = viewingTechnicianResponse.clientName || 'Client';
                    const taskTitle = viewingTechnicianResponse.title || 'Task';
                    const autoReq = viewingTechnicianResponse.clientName ? `${viewingTechnicianResponse.clientName} - ${taskTitle}` : taskTitle;

                    setCreateSurveyFormData(prev => ({
                      ...prev,
                      clientRequest: autoReq,
                      siteName: viewingTechnicianResponse.clientName || 'Branch Site',
                      technicianName: viewingTechnicianResponse.technicianName || profile?.displayName || 'MICHAEL CHUWA',
                      checkingResults: viewingTechnicianResponse.description || ''
                    }));
                    setViewingTechnicianResponse(null);
                    setActiveTab('site_survey');
                    setIsCreateSurveyModalOpen(true);
                  }}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold text-xs flex items-center gap-1.5 cursor-pointer"
                >
                  <Plus className="w-4 h-4" /> Convert to Site Survey
                </button>
                <button
                  onClick={() => setViewingTechnicianResponse(null)}
                  className="px-5 py-2 border border-slate-300 rounded-xl font-bold text-xs text-slate-700 hover:bg-slate-100 cursor-pointer"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ==================== VIEW JOB CARD MODAL ==================== */}
      <AnimatePresence>
        {selectedJobCard && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedJobCard(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden z-20 p-6 space-y-4"
            >
              <div className="flex justify-between items-center border-b pb-3">
                <h3 className="font-bold text-base text-slate-900 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-purple-600" /> Job Card Work Order #{selectedJobCard.id}
                </h3>
                <button onClick={() => setSelectedJobCard(null)}><X className="w-5 h-5 text-slate-400" /></button>
              </div>

              <div className="space-y-3 text-xs text-slate-700">
                <div className="p-3 bg-purple-50 rounded-xl border border-purple-100 space-y-1">
                  <div className="font-extrabold text-purple-900 text-sm">{selectedJobCard.title || selectedJobCard.clientName}</div>
                  <div className="text-purple-700 font-semibold">{selectedJobCard.clientName}</div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-slate-400 font-bold block text-[10px] uppercase">Technician</span>
                    <span className="font-bold text-slate-900">{selectedJobCard.technicianName}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 font-bold block text-[10px] uppercase">Priority</span>
                    <span className="font-bold text-orange-700">{selectedJobCard.priority}</span>
                  </div>
                </div>

                <div>
                  <span className="text-slate-400 font-bold block text-[10px] uppercase">Work Description</span>
                  <p className="p-3 bg-slate-50 rounded-lg border border-slate-200 mt-1">{selectedJobCard.description}</p>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t">
                <button
                  onClick={() => {
                    toast.success(`Job Card printed successfully!`);
                    window.print();
                  }}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl text-xs flex items-center gap-1.5 cursor-pointer"
                >
                  <Printer className="w-4 h-4" /> Print Job Card
                </button>
                <button onClick={() => setSelectedJobCard(null)} className="px-4 py-2 border rounded-xl text-xs font-bold text-slate-700 cursor-pointer">
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      {/* ==================== REQUEST JOB CARD MODAL ==================== */}
      <AnimatePresence>
        {isRequestJobCardModalOpen && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsRequestJobCardModalOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden z-20 border border-slate-200"
            >
              <div className="bg-blue-900 text-white px-6 py-4 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-blue-300" />
                  <h3 className="font-bold text-base">Request Job Card</h3>
                </div>
                <button onClick={() => setIsRequestJobCardModalOpen(false)} className="p-1 hover:bg-blue-800 rounded-lg transition-colors cursor-pointer">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleRequestJobCardSubmit} className="p-6 space-y-4 text-xs font-medium text-slate-700">
                <div className="space-y-1.5">
                  <label className="font-bold text-slate-800">Select Client <span className="text-red-500">*</span></label>
                  <select
                    required
                    value={reqJcClientId}
                    onChange={e => setReqJcClientId(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:ring-2 focus:ring-blue-600 focus:outline-none"
                  >
                    <option value="">Choose client...</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="font-bold text-slate-800">Branch / Site Name</label>
                  <input
                    type="text"
                    value={reqJcBranchName}
                    onChange={e => setReqJcBranchName(e.target.value)}
                    placeholder="e.g. Headquarters / Kariakoo Branch"
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:ring-2 focus:ring-blue-600 focus:outline-none"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="font-bold text-slate-800">Inspection Findings / Scope of Work <span className="text-red-500">*</span></label>
                  <textarea
                    required
                    rows={4}
                    value={reqJcFindings}
                    onChange={e => setReqJcFindings(e.target.value)}
                    placeholder="Describe findings, required service, or equipment details requiring Job Card..."
                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:ring-2 focus:ring-blue-600 focus:outline-none resize-none"
                  />
                </div>

                <div className="flex justify-end items-center gap-3 pt-3 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setIsRequestJobCardModalOpen(false)}
                    className="px-4 py-2 border border-slate-300 text-slate-700 font-bold rounded-xl hover:bg-slate-50 transition-all cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={reqJcSubmitting}
                    className="px-5 py-2 bg-blue-900 hover:bg-blue-800 text-white font-bold rounded-xl shadow-md transition-all cursor-pointer flex items-center gap-2"
                  >
                    {reqJcSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <SendHorizontal className="w-4 h-4" />}
                    <span>Submit Request</span>
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <ServiceScheduleViewerModal
        isOpen={isScheduleViewerOpen}
        onClose={() => setIsScheduleViewerOpen(false)}
      />
    </div>
  );
};

export default TechnicalDeptPage;
